import { useEffect } from 'react';
import { Button, Text, makeStyles, tokens } from '@fluentui/react-components';
import { Dismiss12Filled } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

export interface FileViewerDialogProps {
  open: boolean;
  url: string;
  onClose: () => void;
  title?: string;
}

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  },
  content: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    width: 'min(960px, 100%)',
    height: 'min(90%, 860px)',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: tokens.shadow16,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  title: {
    color: tokens.colorNeutralForeground1,
  },
  frame: {
    flex: 1,
    width: '100%',
    border: 'none',
    backgroundColor: tokens.colorNeutralBackground3,
  },
});

export const FileViewerDialog = ({
  open,
  url,
  onClose,
  title,
}: FileViewerDialogProps) => {
  const styles = useStyles();
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <Text size={400} weight="semibold" className={styles.title}>
            {title ?? t('file', 'File')}
          </Text>
          <Button
            appearance="subtle"
            icon={<Dismiss12Filled />}
            aria-label={t('close', 'Close')}
            onClick={onClose}
          />
        </div>
        <iframe
          className={styles.frame}
          src={url}
          title={title ?? t('file', 'File')}
        />
      </div>
    </div>
  );
};

export default FileViewerDialog;
